import { ACCEPTED_FILE_EXTENSIONS } from '../constants/accepted-file-types';
import type { FileWithPath, FolderTreeNode } from '../types/folder-tree-node';

function hasAcceptedExtension(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return ACCEPTED_FILE_EXTENSIONS.some(ext => lower.endsWith(ext));
}

function normalizeToFileWithPath(
  input: File[] | FileWithPath[]
): FileWithPath[] {
  if (input.length === 0) return [];

  if ('relativePath' in input[0]) {
    return input as FileWithPath[];
  }

  return (input as File[]).map(file => ({
    file,
    relativePath: file.webkitRelativePath || file.name,
  }));
}

export function parseFolderTree(
  input: File[] | FileWithPath[]
): FolderTreeNode[] {
  const normalized = normalizeToFileWithPath(input);

  const validFiles = normalized.filter(({ file }) =>
    hasAcceptedExtension(file.name)
  );

  if (validFiles.length === 0) return [];

  const rootNodes = new Map<string, FolderTreeNode>();
  const allNodes = new Map<string, FolderTreeNode>();

  function getOrCreateNode(
    pathSegments: string[],
    depth: number
  ): FolderTreeNode {
    const path = pathSegments.slice(0, depth + 1).join('/');
    const existing = allNodes.get(path);
    if (existing) return existing;

    const node: FolderTreeNode = {
      name: pathSegments[depth],
      path,
      children: [],
      files: [],
    };
    allNodes.set(path, node);

    if (depth === 0) {
      rootNodes.set(path, node);
    } else {
      const parent = getOrCreateNode(pathSegments, depth - 1);
      if (!parent.children.some(c => c.path === path)) {
        parent.children.push(node);
      }
    }

    return node;
  }

  for (const { file, relativePath } of validFiles) {
    const segments = relativePath.split('/');

    if (segments.length === 1) {
      const rootName = '__root__';
      if (!rootNodes.has(rootName)) {
        const rootNode: FolderTreeNode = {
          name: rootName,
          path: rootName,
          children: [],
          files: [],
        };
        rootNodes.set(rootName, rootNode);
        allNodes.set(rootName, rootNode);
      }
      rootNodes.get(rootName)!.files.push(file);
      continue;
    }

    const folderSegments = segments.slice(0, -1);
    const leafFolder = getOrCreateNode(
      folderSegments,
      folderSegments.length - 1
    );
    leafFolder.files.push(file);
  }

  return Array.from(rootNodes.values());
}

export function countTreeFiles(nodes: FolderTreeNode[]): number {
  let count = 0;
  for (const node of nodes) {
    count += node.files.length;
    count += countTreeFiles(node.children);
  }
  return count;
}
